import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useTodayForecast from '../../hooks/useTodayForecast';
import Button from '../shared/Button';
import Icon from '../shared/Icon';
import ImageScrim from '../shared/ImageScrim';
import PhotoCredit from '../shared/PhotoCredit';
import Shimmer from '../shared/Shimmer';
import WeatherGlyph from '../shared/WeatherGlyph';
import HeroPhotoDrift from './HeroPhotoDrift';
import navLogo from '../../assets/blue-lyf-transparent-1024.png';
import { formatDateRangeShort, getDisplayLocale, getLocalDateString } from '../../utils/dateUtils';
import { inviteCover } from '../../utils/pendingInvites';
import { inviteTitle } from '../../utils/inviteTitle';
import { heroPhotos, tripCover, tripCenter, tripLength, tripPlaceName } from '../../utils/homePlans';
import { Countdown, PlanFact, daysUntil } from './planCopy';

// The forecast runs about a week ahead; a trip further out gets no weather
// line (a typical-month figure in a hero would read as a forecast).
const FORECAST_DAYS = 8;

function greetingKey(hour) {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

// The greeting follows the clock while Home stays open: Home is the tab people
// return to, often hours later, and "Good morning" at 9pm reads as broken.
// Checked each minute and whenever the app comes back to the foreground.
function useGreetingKey() {
  const [key, setKey] = useState(() => greetingKey(new Date().getHours()));
  useEffect(() => {
    const update = () => setKey(greetingKey(new Date().getHours()));
    const timer = setInterval(update, 60 * 1000);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);
  return key;
}

const listNames = (names, locale) => {
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
};

const firstName = (name) => String(name || '').trim().split(/\s+/)[0];

/** Companions on the trip, first names, you excluded: "with Sam and Mila" / "with Sam, Mila and 2 others". */
function companionsLine(trip, currentUserId, t) {
  const others = (trip.travelers || [])
    .filter(p => p.id !== currentUserId && p.status === 'accepted' && p.name)
    .map(p => firstName(p.name));
  if (others.length === 0) return null;
  const shown = others.slice(0, 2);
  const rest = others.length - shown.length;
  const names = rest > 0 ? [...shown, t('hero.othersCount', { count: rest })] : shown;
  return t('hero.with', { names: listNames(names, getDisplayLocale()) });
}

function Chip({ warm = false, children }) {
  return warm
    ? <span className="px-2.5 h-6 inline-flex items-center rounded-full bg-coral text-coral-ink text-xs font-bold whitespace-nowrap">{children}</span>
    : <span className="px-2.5 h-6 inline-flex items-center rounded-full bg-white/90 text-primary-deep text-xs font-semibold whitespace-nowrap">{children}</span>;
}

function HeroSkeleton() {
  return (
    <div className="absolute inset-0 max-w-7xl mx-auto px-4 py-5 flex flex-col justify-end gap-3" aria-hidden="true">
      <Shimmer className="h-5 w-32 bg-surface/60 rounded-lg" />
      <Shimmer className="h-12 w-64 bg-surface/60 rounded-lg" />
      <Shimmer className="h-4 w-80 max-w-full bg-surface/60 rounded-lg" />
      <div className="flex gap-2 mt-2"><Shimmer className="h-11 w-32 bg-surface/60 rounded-lg" /><Shimmer className="h-11 w-36 bg-surface/60 rounded-lg" /></div>
    </div>
  );
}

/**
 * The hero: the one trip the ladder picked (homePlans.buildPlans), its cover
 * as the photo, or — with nothing planned — the home city this weekend with
 * the weekend's lead photo. The greeting and the search button sit in its top
 * row (phones have no top nav, so the logo lives here too). Never a blank:
 * one trip or five, the hero is full.
 */
export default function HomeHero({ hero, loading, error, onRetry, homeCity, homeCoords, weekendPlace = homeCity, weekendAway = false, weekendItems = [], weekendCount = 0, weekendScope = 'soon', onSearch, onOpenInvite, onJumpToAway, onJumpToWeekend }) {
  const { t } = useTranslation('home');
  const { currentUser } = useAuth();
  const greeting = useGreetingKey();
  const today = getLocalDateString(new Date());
  const name = firstName(currentUser?.name);

  const homeWeather = useTodayForecast(homeCoords, homeCity);
  const trip = hero?.trip || null;
  const center = trip ? tripCenter(trip) : null;
  const tripStart = (trip?.startDate || trip?.start_date || '').slice(0, 10);
  const until = trip ? daysUntil(trip, today) : null;
  const forecastDate = trip && until !== null && until >= 0 && until <= FORECAST_DAYS ? tripStart : (hero?.kind === 'happening' ? today : null);
  const tripWeather = useTodayForecast(forecastDate && center ? center : null, center?.name, forecastDate || today);

  const dateLine = new Date().toLocaleDateString(getDisplayLocale(), { weekday: 'long', day: 'numeric', month: 'long' });
  const greetingText = name ? t(`greeting.${greeting}`, { name }) : t(`greeting.${greeting}NoName`);
  const weatherText = homeWeather ? t('hero.weather', { high: homeWeather.high }) : null;

  // What the hero shows, by kind. primary / secondary are { to | onClick, label }.
  let photo = null;
  let kicker = null;
  let chips = null;
  let title = '';
  let line = null;
  let fact = null;
  let primary = null;
  let secondary = null;

  const kind = hero?.kind || 'home';
  // On a trip under way or just over, the hero drifts through the trip's own
  // photos. Shuffled once per trip and photo list: a plans refetch hands over
  // a new trip object with the same photos, and must not reorder the set
  // under the photo on screen.
  const rotates = !!trip && (kind === 'happening' || kind === 'justEnded');
  const photoListKey = rotates ? [tripCover(trip), ...(trip.hero_photos || [])].join('\n') : '';
  const tripPhotoSet = useMemo(
    () => (rotates ? heroPhotos(trip) : null),
    [rotates, trip?.id, photoListKey] // eslint-disable-line react-hooks/exhaustive-deps
  );
  if (trip) {
    photo = tripCover(trip) || tripPhotoSet?.[0] || null;
    const tripId = encodeURIComponent(trip.id);
    const start = trip.startDate || trip.start_date;
    const end = trip.endDate || trip.end_date || start;
    const stops = trip.activity_count || 0;
    const companions = companionsLine(trip, currentUser?.id, t);
    const parts = [];
    if (start) parts.push(formatDateRangeShort(start, end));
    if (start && kind !== 'happening') parts.push(t('hero.days', { count: tripLength(trip) }));
    if (stops > 0) parts.push(t('hero.stops', { count: stops }));
    if (trip.stay?.name && !trip.stay.is_home) parts.push(t('hero.stayingAt', { name: trip.stay.name }));
    line = parts.join(t('hero.separator'));
    title = trip.name || tripPlaceName(trip);
    fact = hero.fact;

    if (kind === 'happening') {
      kicker = t('hero.kicker.happening');
      chips = <><Chip warm>{t('plans.countdown.today')}</Chip>{companions && <Chip>{companions}</Chip>}</>;
      primary = { to: `/trip/${tripId}`, label: t('hero.action.todaysPlan') };
      secondary = center && { onClick: () => onJumpToAway(trip.id), label: t('hero.action.ideasForToday') };
    } else if (kind === 'justEnded') {
      kicker = t('hero.kicker.justEnded');
      title = t('hero.howWas', { name: trip.name || tripPlaceName(trip) });
      chips = companions && <Chip>{companions}</Chip>;
      primary = { to: `/trip/${tripId}?tab=recap`, label: t('hero.action.finishRecap') };
      secondary = { to: `/trip/${tripId}`, label: t('hero.action.openTrip') };
    } else if (kind === 'idea') {
      kicker = t('hero.kicker.idea');
      chips = <><Chip>{t('hero.noDatesYet')}</Chip>{companions && <Chip>{companions}</Chip>}</>;
      primary = { to: `/trip/${tripId}`, label: t('hero.action.pickDates') };
      secondary = center && { onClick: () => onJumpToAway(trip.id), label: t('hero.action.moreIdeas') };
    } else {
      kicker = t('hero.kicker.upcoming');
      chips = <><Chip warm><Countdown days={until} /></Chip>{companions && <Chip>{companions}</Chip>}</>;
      primary = { to: `/trip/${tripId}`, label: t('hero.action.openPlan') };
      const emptyDay = fact && (fact.key === 'emptyDay' || fact.key === 'emptyDays' || fact.key === 'nothingPlanned');
      secondary = center && {
        onClick: () => onJumpToAway(trip.id),
        label: emptyDay ? t('hero.action.ideasForDay', { index: fact.index || 1 }) : t('hero.action.ideasForTrip'),
      };
    }
  } else if (kind === 'invite' && hero.invite) {
    const invite = hero.invite;
    photo = inviteCover(invite);
    kicker = t('hero.kicker.invite');
    title = inviteTitle(invite.title, { trip: invite.type === 'trip' });
    const when = invite.type === 'trip'
      ? (invite.start_date && invite.end_date ? formatDateRangeShort(invite.start_date, invite.end_date) : t('comingUp.invites.datesTbd'))
      : (invite.scheduled_date ? formatDateRangeShort(invite.scheduled_date, invite.scheduled_date) : t('comingUp.invites.datesTbd'));
    line = [when, invite.location, invite.going_count > 0 ? t('comingUp.invites.going', { count: invite.going_count }) : null]
      .filter(Boolean).join(t('hero.separator'));
    chips = <span className="px-2.5 h-6 inline-flex items-center rounded-full bg-coral-light text-coral-dark text-xs font-bold">{t('rail.invitedBy', { name: invite.from_user_name })}</span>;
    primary = { onClick: () => onOpenInvite(invite), label: t('hero.action.answerInvite') };
  } else {
    // Nothing planned: the home city this weekend, with the weekend's lead
    // photo — or, with the phone away from home, wherever the weekend's list
    // is fenced (weekendPlace, "Near you" when unnamed).
    // A show demoted for being far from the phone (_awayFromCenter, a
    // followed show back home) never fronts it: photo, chips and the count
    // (Home's weekendCount) are what is around the phone.
    const near = weekendItems.filter(i => !i._awayFromCenter);
    const lead = near.find(i => i.cover_photo || i.image_url);
    photo = lead ? (lead.cover_photo || lead.image_url) : null;
    kicker = weekendAway
      ? t(weekendScope === 'next' ? 'hero.kicker.awayNext' : 'hero.kicker.away')
      : t(weekendScope === 'next' ? 'hero.kicker.homeNext' : 'hero.kicker.home');
    title = weekendPlace || t('hero.nearYou');
    line = weekendCount > 0 ? t('hero.thingsOn', { count: weekendCount }) : t('hero.nothingPlanned');
    chips = near.slice(0, 3).map((item, i) => (
      <Chip key={item.id || i}>{item.title || item.name}</Chip>
    ));
    primary = { to: '/day-out/new', label: t('hero.action.planDayOut') };
    secondary = weekendCount > 0 && {
      onClick: onJumpToWeekend,
      label: t(weekendScope === 'next' ? 'hero.action.seeNextWeekend' : 'hero.action.seeWeekend'),
    };
  }

  const onPhoto = !!photo;
  const text = onPhoto ? 'text-white' : 'text-content-primary';
  const muted = onPhoto ? 'text-white/90' : 'text-content-secondary';
  const photoSet = tripPhotoSet?.length ? tripPhotoSet : (photo ? [photo] : []);
  // Over a photo the actions are small glass pills that let the photo through
  // and size to their labels; with no photo, the usual buttons. A pill draws
  // at 36px but takes taps 4px past each edge (after:-inset-1), the 44px touch
  // minimum; the row's 8px gap keeps the two tap areas from overlapping.
  const action = (a, main) => a && (
    <Button
      to={a.to}
      onClick={a.onClick}
      variant={onPhoto ? (main ? 'photo' : 'photoGlass') : (main ? 'primary' : 'secondary')}
      size={onPhoto ? 'sm' : 'md'}
      shape={onPhoto ? 'pill' : 'default'}
      className={onPhoto ? 'relative after:absolute after:-inset-1 after:rounded-full' : ''}
    >
      {a.label}
    </Button>
  );
  const weatherLine = tripWeather && center
    ? t('hero.placeWeather', { place: center.name, high: tripWeather.high })
    : null;

  return (
    <section
      aria-label={t('hero.ariaLabel')}
      className={`relative h-[420px] md:h-[440px] overflow-hidden ${onPhoto ? 'bg-surface-dark' : 'bg-primary-lighter'}`}
    >
      {photo && (
        <HeroPhotoDrift
          key={trip?.id || 'hero'}
          photos={photoSet}
          overlay={(src) => (
            <>
              <ImageScrim className="h-3/4 from-black/80 via-black/35" />
              <PhotoCredit src={src} size="lg" />
            </>
          )}
        />
      )}

      {/* pb-8 on phones: the credit strip runs along the bottom edge (one
          line, ~22px); the buttons sit just above it, not under
          its links, and as low as they can so they cover less photo. */}
      <div className={`absolute inset-0 max-w-7xl mx-auto px-4 pt-4 ${photo ? 'pb-8' : 'pb-4'} md:py-6 flex flex-col justify-between`}>
        {/* Top row: logo + greeting (phones), greeting · date · weather (desktop), search */}
        <div className="flex items-center justify-between gap-3">
          <div className={`flex items-center gap-2.5 min-w-0 ${text}`}>
            <img src={navLogo} alt={t('greeting.logoAlt')} className="w-8 h-8 flex-shrink-0 md:hidden" />
            <p className="text-[15px] font-semibold truncate">
              {greetingText}
              <span className={`hidden md:inline font-medium ${muted}`}>
                {t('hero.separator')}{dateLine}
                {homeCity && <>{t('hero.separator')}{homeCity}</>}
                {weatherText && (
                  <>
                    {' '}
                    <WeatherGlyph condition={homeWeather.condition || homeWeather.conditions} className="w-4 h-4 inline-block align-[-3px]" />
                    {' '}{weatherText}
                  </>
                )}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onSearch}
            aria-label={t('search.open')}
            className="flex-shrink-0 h-10 md:h-11 md:w-72 px-0 md:px-4 w-10 rounded-full bg-white/90 text-primary-deep shadow-card flex items-center justify-center md:justify-start gap-2 hover:bg-white transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
          >
            <Icon name="search" className="w-5 h-5" />
            <span className="hidden md:inline text-sm font-medium text-content-secondary truncate">{t('search.prompt')}</span>
          </button>
        </div>

        {loading ? <HeroSkeleton /> : error ? (
          <div className={`flex flex-col gap-3 ${text}`}>
            <p className="text-base">{error}</p>
            <div><Button variant="secondary" onClick={onRetry}>{t('hero.retry')}</Button></div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className={`flex flex-col gap-2 max-w-2xl min-w-0 ${text}`}>
              <div className="flex items-center flex-wrap gap-2">
                {kicker && <span className={`text-[11px] md:text-xs font-bold tracking-[.08em] uppercase ${muted}`}>{kicker}</span>}
                {chips}
              </div>
              <h1 className="font-display font-medium text-4xl md:text-6xl leading-none [text-wrap:balance] break-words">{title}</h1>
              {line && <p className={`text-sm md:text-base ${muted}`}>{line}</p>}
              {(fact || weatherLine) && (
                <p className={`text-sm md:text-base ${muted}`}>
                  {fact && <PlanFact fact={fact} />}
                  {fact && weatherLine && t('hero.separator')}
                  {weatherLine && (
                    <>
                      <WeatherGlyph condition={tripWeather.condition || tripWeather.conditions} className="w-4 h-4 inline-block align-[-3px] mr-1" />
                      {weatherLine}
                    </>
                  )}
                </p>
              )}
            </div>
            {/* One set of buttons, primary first on a phone and last in the
                right-aligned desktop pair. On a photo they keep to their
                labels; with no photo they split a phone's width in halves. */}
            <div className={`flex gap-2 mt-1 md:mt-0 md:flex-row-reverse md:flex-shrink-0 ${onPhoto ? '' : '[&>*]:flex-1 md:[&>*]:flex-none'}`}>
              {action(primary, true)}
              {action(secondary, false)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
